import * as React from 'react';
import { EventRowMLB } from './eventRowMLB/EventRowMLB';
import { EventRowNBA } from './eventRowNBA/EventRowNBA';
import { EventRowNFL } from './eventRowNFL/EventRowNFL';
import { EventRowNHL } from './eventRowNHL/EventRowNHL';
import { EventGroupHeaderMLB } from './EventGroupHeaderMLB';
import { EventGroupHeaderNBA } from './EventGroupHeaderNBA';
import { EventGroupHeaderNFL } from './EventGroupHeaderNFL';
import { EventGroupHeaderNHL } from './EventGroupHeaderNHL';
import { checkCompetition } from 'src/domains/sportsbook/utils/checkCompetition';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRow6Pack/EventRow6Pack.state';
import { MLBEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowMLB/EventRowMLB.state';
import { NBAEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNBA/EventRowNBA.state';
import { NFLEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNFL/EventRowNFL.state';
import { NHLEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNHL/EventRowNHL.state';

export interface AmericanLeagueRowFlagsType {
    readonly isBaseball: boolean;
    readonly isMLBorMajorCompetition: boolean;
    readonly isNHL: boolean;
    readonly isNBA: boolean;
    readonly isNFL: boolean;
}

export type EventRowLogoStateConstructor = new (eventId: EventId, language: LanguagesState) => EventRow6PackState;

export function getAmericanLeagueRowFlags(competitionName: string, sport: string): AmericanLeagueRowFlagsType {
    return {
        isBaseball: sport === 'baseball',
        isMLBorMajorCompetition: checkCompetition(competitionName, ['MLB', 'Major']) && sport === 'baseball',
        isNHL: checkCompetition(competitionName, ['NHL']),
        isNBA: checkCompetition(competitionName, ['NBA']),
        isNFL: competitionName === 'US NFL' || competitionName === 'NFL Matches' || competitionName === 'NFL',
    };
}

const isAmericanSixPackCompetition = (flags: AmericanLeagueRowFlagsType): boolean => {
    return flags.isBaseball === true || flags.isNHL === true || flags.isNBA === true || flags.isNFL === true;
};

/**
 * Temporary: for Genius sports only, keep American 6-pack competitions on EventRowUnified
 * (1 market + logos). Sports not in `geniusSports` keep classic Money Line / Spread / Total.
 */
export const shouldUseUnifiedAmericanSportRow = (
    flags: AmericanLeagueRowFlagsType,
    sportId: string,
    geniusSports: ReadonlyArray<string>
): boolean => {
    return isAmericanSixPackCompetition(flags) === true && geniusSports.includes(sportId);
};

export const getAmericanSportLogoStateConstructor = (
    flags: AmericanLeagueRowFlagsType,
    event: EventListGroupEventItemType
): EventRowLogoStateConstructor | null => {
    if (event.template.startsWith('two-participant') === false) {
        return null;
    }

    if (flags.isNBA === true) {
        return NBAEventRow6PackState;
    }

    if (flags.isNFL === true) {
        return NFLEventRow6PackState;
    }

    if (flags.isNHL === true) {
        return NHLEventRow6PackState;
    }

    if (flags.isBaseball === true) {
        return MLBEventRow6PackState;
    }

    return null;
};

export function renderAmericanLeagueEventRowIfApplicable(
    event: EventListGroupEventItemType,
    flags: AmericanLeagueRowFlagsType,
    keyPrefix: string
): React.ReactElement | null {
    const isTwoParticipant = event.template.startsWith('two-participant');
    const key = `${keyPrefix}_${event.id}`;

    if (flags.isBaseball && isTwoParticipant) {
        return (
            <EventRowMLB
                key={key}
                eventId={event.id2}
                smallEvent={event}
                showParticipantLogos={flags.isMLBorMajorCompetition}
            />
        );
    }
    if (flags.isNHL && isTwoParticipant) {
        return (
            <EventRowNHL
                key={key}
                eventId={event.id2}
                smallEvent={event}
            />
        );
    }
    if (flags.isNBA && isTwoParticipant) {
        return (
            <EventRowNBA
                key={key}
                eventId={event.id2}
                smallEvent={event}
            />
        );
    }
    if (flags.isNFL) {
        return (
            <EventRowNFL
                key={key}
                eventId={event.id2}
                smallEvent={event}
            />
        );
    }

    return null;
}

interface AmericanLeagueGroupHeaderPropsType {
    competitionName: string;
    sport: string;
    areEventsTwoParticipantsOnly: boolean;
    dataTest?: string;
    label: string;
    onToggle: () => void;
    open: boolean;
}

export function renderAmericanLeagueGroupHeaderIfApplicable(
    props: AmericanLeagueGroupHeaderPropsType
): React.ReactElement | null {
    const { competitionName, sport, areEventsTwoParticipantsOnly, dataTest, label, onToggle, open } = props;
    const flags = getAmericanLeagueRowFlags(competitionName, sport);
    const headerProps = { dataTest, label, onToggle, open };

    if (flags.isBaseball && areEventsTwoParticipantsOnly) {
        return <EventGroupHeaderMLB {...headerProps} />;
    }
    if (flags.isNHL && areEventsTwoParticipantsOnly) {
        return <EventGroupHeaderNHL {...headerProps} />;
    }
    if (flags.isNBA && areEventsTwoParticipantsOnly) {
        return <EventGroupHeaderNBA {...headerProps} />;
    }
    if (flags.isNFL) {
        return <EventGroupHeaderNFL {...headerProps} />;
    }

    return null;
}
